import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import ProgressBar from '@atlaskit/progress-bar';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { Status, type MigrationStatus } from '@atlassian/jira-project-create-importer-common';
import { CONTACT_SUPPORT_URL } from '../../../../../common/constants';
import messages from './messages';

const formatProgress = (progress: number) => `${(progress * 100).toFixed(0)}%`;

export type Props = {
	migrationStatus: MigrationStatus;
	onDownloadClick: () => void;
};

export const Footer = ({ migrationStatus, onDownloadClick }: Props) => {
	const { formatMessage } = useIntl();
	const { overallProgress: progress, overallStatus: status, hasErrorLogs } = migrationStatus;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onContactSupportClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'contactSupportLink',
			}),
		);
	}, [createAnalyticsEvent]);

	if (status === Status.FAILED) {
		return (
			<Inline>
				{hasErrorLogs && (
					<>
						<LinkButton appearance="link" spacing="compact" onClick={onDownloadClick}>
							{formatMessage(messages.downloadLogButton)}
						</LinkButton>
						<Box xcss={dotStyles} />
					</>
				)}
				<LinkButton
					appearance="link"
					spacing="compact"
					href={CONTACT_SUPPORT_URL}
					target="_blank"
					onClick={onContactSupportClick}
				>
					{formatMessage(messages.contactSupport)}
				</LinkButton>
			</Inline>
		);
	}

	const label = formatMessage(
		status === Status.SUCCESS ? messages.progressCompleteText : messages.progressText,
	);

	return (
		<Stack xcss={progressContainerStyles}>
			<Inline xcss={progressTextStyles}>
				<Box aria-hidden>{label}</Box>
				<Box aria-hidden>{formatProgress(progress)}</Box>
			</Inline>
			<ProgressBar
				ariaLabel={label}
				value={progress}
				appearance={status === Status.SUCCESS ? 'success' : 'default'}
				testId="project-create-import-progress.ui.import-progress-button.progress-button.popover-content.footer.progress-bar"
			/>
		</Stack>
	);
};

const progressContainerStyles = xcss({
	width: '100%',
});

const progressTextStyles = xcss({
	justifyContent: 'space-between',
	fontSize: '14px',
	fontStyle: 'normal',
	fontWeight: 400,
	lineHeight: '20px',
	color: 'color.text.subtle',
	paddingBottom: 'space.050',
});

const dotStyles = xcss({
	height: '4px',
	width: '4px',
	borderRadius: 'border.radius.050',
	backgroundColor: 'color.background.accent.gray.bolder.hovered',
	alignSelf: 'center',
	marginRight: 'space.100',
	marginLeft: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LinkButton = styled(Button)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	paddingLeft: '0 !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	paddingRight: '0 !important',
});
